import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosResponseHeaders } from 'axios';
import { API } from 'network/api';

interface FarmerReqParams {
  page: number;
  size: number;
  'country.equals'?: string;
  'name.contains'?: string;
  'status.equals'?: string;
  'contractStatus.equals'?: string;
}

const fetchFarmers = async (options: FarmerReqParams, signal: AbortSignal) => {
  try {
    const res = await API.get(`farmers`, {
      params: options,
      signal,
    });

    if (!res?.data)
      throw new Error(
        'Error occured while trying to fetch farmers information. Please try again',
      );

    if (res?.data?.length === 0) throw new Error('No data available');

    return {
      data: res?.data,
      headers: res?.headers,
    };
  } catch (err) {
    if (err instanceof Error) throw err;
    throw new Error('Something went wrong');
  }
};

type UseFarmersQueryArgs = Omit<FarmerReqParams, 'page' | 'size'>;

const useFarmersQuery = (args: UseFarmersQueryArgs) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const queryClient = useQueryClient();

  const options = useMemo(
    () => ({ ...args, page: currentPage - 1, size: pageSize }),
    [args, currentPage, pageSize],
  );

  const {
    data: response,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['farmers', options],
    queryFn: ({ signal }) => fetchFarmers(options, signal),
    staleTime: 1000 * 10, // 10 seconds
    retry: 1,
  });

  const data = response?.data;
  const headers = response?.headers as AxiosResponseHeaders;
  const total = Number(headers?.get('X-Total-Count')) || 0;

  const handlePageChange = useCallback((page: number, pageSize: number) => {
    setCurrentPage(page);
    setPageSize(pageSize);
  }, []);

  const prefetch = (args: Partial<FarmerReqParams>) => {
    return queryClient.prefetchQuery({
      queryKey: [
        'farmers',
        {
          ...options,
          ...args,
        },
      ],
      queryFn: ({ signal }) =>
        fetchFarmers(
          {
            ...options,
            ...args,
          },
          signal,
        ),
      staleTime: 1000 * 10, // 10 seconds
    });
  };
  // Prefetch next page data ahead of without blocking current page network calls
  // Done inorder to improve user experience
  useEffect(() => {
    if (!isFetching && !isLoading && data?.length > 0 && currentPage < total) {
      prefetch({
        page: currentPage,
        size: pageSize,
      });
    }
  }, [data, currentPage, pageSize, isLoading, isFetching]);

  return {
    isLoading,
    isError,
    data,
    error,
    headers,
    total,
    currentPage,
    pageSize,
    refetch,
    handlePageChange,
    setCurrentPage,
    setPageSize,
  };
};

export default useFarmersQuery;
